import { useEffect, useRef, useState, useContext } from 'react';
import { widget } from '../charting_library'; // same path you already use
import FuturesDatafeed from './datafeedFutures';
import './index.css';
import { SocketContext } from '../../SocketContext';
import { disconnectFuturesChartSocket, setSharedSocket } from './streamingFutures';
import {
  applyChartThemeToWidget,
  CHART_BG_DARK,
  CHART_BG_LIGHT,
  getChartThemeFromBody,
} from '../chartThemeFromBody';

const FUTURES_CHART_CONTAINER = '#TVFuturesChartContainer';

/**
 * Usage:
 *  <TVFuturesChartContainer symbol="BTCUSDT_PERP" />
 *  (Symbol format: BASEQUOTE_PERP for perpetuals, e.g., ETHUSDT_PERP)
 */
export default function TVFuturesChartContainer({ symbol }) {
  const { getSocket, isConnected } = useContext(SocketContext);
  const [tvWidget, setTvWidget] = useState();
  const [bodyChartTheme, setBodyChartTheme] = useState(getChartThemeFromBody);
  const initOnceRef = useRef(false);
  const tvWidgetRef = useRef(null);
  tvWidgetRef.current = tvWidget;

  // Use same socket as UsdMFutures (futures:update already subscribed there)
  useEffect(() => {
    if (isConnected) {
      const socket = getSocket();
      if (socket) setSharedSocket(socket);
    }
  }, [isConnected, getSocket]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setBodyChartTheme(getChartThemeFromBody());
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const initChart = (symbol) => {
    const containerId = 'TVFuturesChartContainer';
    const container = document.getElementById(containerId);
    if (!container) {
      setTimeout(() => initChart(symbol), 300);
      return;
    }

    const Theme = getChartThemeFromBody();
    const isMobile = window.innerWidth <= 480;
    const isLight = Theme === 'light';
    const textColor = isLight ? '#1a1a1a' : '#ffffff';
    const bgPane = isLight ? '#ffffff' : '#181A20';

    const options = {
      symbol,
      interval: '1',
      container: containerId,
      datafeed: FuturesDatafeed, // <= FUTURES DATAFEED
      library_path: '/charting_library/',
      timezone: 'Asia/Kolkata',
      has_intraday: true,
      intraday_multipliers: ['1', '60'],
      height: isMobile ? '400px' : '625px',
      load_last_chart: true,
      fullscreen: false,
      pricescale: 100000000,
      custom_css_url: 'css/style.css',
      hide_resolution_in_legend: true,
      time_frames: [
        { text: '1D', resolution: 'D', description: '1 Day' },
        { text: '1W', resolution: 'W', description: '1 Week' },
        { text: '1M', resolution: 'M', description: '1 Month' },
      ],
      time_scale: { min_bar_spacing: 1 },
      theme: isLight ? 'light' : 'dark',
      disabled_features: [
        'use_sessionStorage_for_settings', 'adaptive_logo',
        'border_around_the_chart', 'header_symbol_search',
        'header_interval_dialog_button', 'header_compare',
        'header_undo_redo', 'header_resolutions',
        'left_toolbar'
      ],
      overrides: {
        'scalesProperties.fontSize': 14,
        'scalesProperties.fontFamily': 'HarmonyOS Sans Regular, sans-serif',
        'scalesProperties.textColor': textColor,
        'paneProperties.legendProperties.showLegend': true,
        'paneProperties.legendProperties.showStudyValues': true,
        'paneProperties.legendProperties.fontSize': 14,
        'paneProperties.legendProperties.fontFamily': 'HarmonyOS Sans Regular, sans-serif',
      },
      styleOverrides: {
        'paneProperties.background': bgPane,
        'paneProperties.backgroundType': 'solid',
      },
      loading_screen: {
        backgroundColor: bgPane,
      },
    };

    const w = new widget(options);

    w.onChartReady(() => {
      try {
        const chart = w.chart?.();
        if (!chart) return;

        applyChartThemeToWidget(w, FUTURES_CHART_CONTAINER);

        // Remove volume from chart
        const studies = chart.getAllStudies?.() || [];
        studies.forEach(study => {
          if (study?.name?.toLowerCase?.().includes('volume')) {
            chart.removeEntity(study.id);
          }
        });

        // === Custom timeframe buttons ===
        w.headerReady?.().then(() => {
          try {
            const ch = w.chart?.();
            if (!ch) return;
            const intervals = [
          { value: '1', label: '1 Min' },
          { value: '5', label: '5 Min' },
          { value: '15', label: '15 Min' },
          { value: '30', label: '30 Min' },
          { value: '60', label: '1 Hour' },
          { value: 'D', label: '1 Day' },
          { value: 'W', label: '1 Week' },
          { value: 'M', label: '1 Month' },
        ];
            intervals.forEach(i => {
              const btn = w.createButton?.();
              if (!btn) return;
              btn.classList.add('custom-interval-button');
              btn.title = `Switch to ${i.label}`;
              btn.textContent = i.label;
              btn.addEventListener('click', () => {
                try {
                  w.chart?.()?.setResolution(i.value);
                } catch (err) {}
              });
            });
          } catch (err) {}
        });
      } catch (e) {
        // Chart API may not be ready
      }
    });

    setTvWidget(w);
  };

  useEffect(() => {
    if (!initOnceRef.current && symbol) {
      initChart(symbol);
      initOnceRef.current = true;
    }
  }, [symbol]);

  // Cleanup only on unmount (not when symbol changes)
  useEffect(() => {
    return () => {
      disconnectFuturesChartSocket();
      const w = tvWidgetRef.current;
      if (w) {
        try {
          w.remove();
        } catch (e) {
          console.warn("Error removing futures TV widget:", e);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!tvWidget) return;
    const apply = async () => {
      try {
        await tvWidget._innerWindowLoaded;
        applyChartThemeToWidget(tvWidget, FUTURES_CHART_CONTAINER);
      } catch (e) {
        console.error('Error applying futures chart theme from body class:', e);
      }
    };
    apply();
  }, [bodyChartTheme, tvWidget]);

  // change symbol live
  useEffect(() => {
    if (!tvWidget || !symbol) return;
    tvWidget.onChartReady(() => {
      try {
        const chart = tvWidget.chart?.();
        if (chart) chart.setSymbol(symbol, () => null);
      } catch (e) {
        // Widget may have been removed or chart not ready
      }
    });
  }, [symbol, tvWidget]);

  const isMobile = window.innerWidth <= 480;
  const chartHeight = isMobile ? '400px' : '625px';
  const bgColor = bodyChartTheme === 'light' ? CHART_BG_LIGHT : CHART_BG_DARK;

  return (
    <div
      style={{
        position: "relative",
        minHeight: chartHeight,
        height: chartHeight,
        width: '100%',
        backgroundColor: bgColor,
      }}
    >
      <div
        id="TVFuturesChartContainer"
        style={{
          width: '100%',
          height: chartHeight,
          minHeight: chartHeight,
          backgroundColor: bgColor,
        }}
      />
    </div>
  );
}
